import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Scrollbars } from "react-custom-scrollbars";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import Preloader from "@components/preloader";

import pluralize from "ati-utils/pluralize";

import Notification from "../Notification";

import animate from "../../../../utils/animate";
import getAndroidVersion from "../../../../utils/getAndroidVersion";

import { useStore } from "../../../../connect";

import strings from "../../../../locales/strings";

import styles from "./NotificationsScrollbar.scss";

const cx = classnames.bind(styles);

const mobileControlsHeightPx = 287;
const desktopControlsHeightPx = 287;
const preloaderSpinnerHeight = 44;

const isOverscrollBehaviorEnabled =
  window.CSS && !window.CSS.supports("overscroll-behavior", "none");

const scrollPositions = {
  top: 0,
  bottom: 1,
};

const androidVersion = getAndroidVersion();

const NotificationsScrollbar = ({ mobile }) => {
  const { notifications, baseUrl, locale } = useStore();

  const scrollbar = useRef(null);
  const startY = useRef(null);
  const notificationsRefs = useRef({});

  const preventScroll = (event) => {
    const { top, clientHeight, scrollHeight } = scrollbar.current.getValues();
    const deltaY = event.deltaY || event.detail || event.wheelDelta;

    if (
      (top === 0 && deltaY < 0) ||
      (top === 1 && deltaY > 0) ||
      scrollHeight <= clientHeight
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const addWheelListeners = () => {
    if (scrollbar.current) {
      scrollbar.current.container.addEventListener("wheel", preventScroll, {
        passive: false,
      });

      scrollbar.current.container.addEventListener(
        "mousewheel",
        preventScroll,
        { passive: false }
      );
    }
  };

  const removeWheelEventListeners = () => {
    if (scrollbar.current) {
      scrollbar.current.container.removeEventListener("wheel", preventScroll, {
        passive: false,
      });

      scrollbar.current.container.removeEventListener(
        "mousewheel",
        preventScroll,
        { passive: false }
      );
    }
  };

  const touchStartHandler = (event) => {
    startY.current = event.touches.clientY;
  };

  const touchMoveHandler = (event) => {
    const { top } = scrollbar.current.getValues();
    const clientY = event.touches[0].clientY;
    if (
      (top === scrollPositions.top && startY.current > clientY) ||
      (top === scrollPositions.bottom && startY.current < clientY)
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
    startY.current = clientY;
  };

  const addTouchListeners = () => {
    if (scrollbar.current) {
      scrollbar.current.container.addEventListener(
        "touchstart",
        touchStartHandler,
        { passive: false }
      );

      scrollbar.current.container.addEventListener(
        "touchmove",
        touchMoveHandler,
        { passive: false }
      );
    }
  };

  const removeTouchEventListeners = () => {
    if (scrollbar.current) {
      scrollbar.current.container.removeEventListener(
        "touchstart",
        touchStartHandler,
        { passive: false }
      );

      scrollbar.current.container.removeEventListener(
        "touchmove",
        touchMoveHandler,
        { passive: false }
      );
    }
  };

  const shouldShowPreloader = () =>
    (notifications.notificationsTotal > 0 ||
      notifications.archivedNotificationsLoadMore) &&
    !notifications.extraNewNotifications;

  const handleScrollbar = (data = scrollbar.current.getValues()) => {
    /** Вьюпорт по нижней границе выпадашки с уведомлениями. */
    const viewport = data.scrollTop + data.clientHeight;

    const hasUnreadNotifications = notifications.notifications.some(
      (item) => !!item.IsUnread
    );

    if (hasUnreadNotifications && viewport) {
      notifications.notifications.forEach((notification) => {
        if (notification.IsUnread) {
          const id = notification.ID;
          const ref = notificationsRefs.current[id];

          if (ref) {
            const itemBottomPoint = ref.offsetTop + ref.offsetHeight;
            const outOfViewport =
              itemBottomPoint > viewport || itemBottomPoint < data.scrollTop;

            if (!outOfViewport) {
              notifications.readNotificationsDebounce(id);
            }
          }
        }
      });
    }

    if (
      /** Если проскроллили до верхней и далее границы спиннера */
      data.scrollHeight - preloaderSpinnerHeight <= viewport &&
      shouldShowPreloader() &&
      /** Если уже запрашиваем часть уведомлений, чтоб недопустить парралельной загрузки */
      !notifications.notificationsReadRequest &&
      /** Если есть сверхновые уведомления отключаем подгрузку по скроллу */
      !hasUnreadNotifications
    ) {
      notifications.loadMoreNotifications();
    }
  };

  const handleScrollFrame = (data) => {
    handleScrollbar(data);
  };

  const getExtraNewNotificationsText = () => {
    const label = pluralize(
      strings.notificationsPluralize,
      notifications.extraNewNotifications,
      locale
    );

    return (
      <span>
        <svg className={cx("caret")} width="13" height="9" viewBox="0 0 13 9">
          <path
            fill="var(--glz-color-neutral-tone-5)"
            fillRule="evenodd"
            d="M1.622 8.27c.193.2.5.206.7-.002l4.524-4.705 4.524 4.705a.485.485 0 0 0 .7.002l.806-.839a.528.528 0 0 0 .006-.72L6.846.435.81 6.712a.528.528 0 0 0 .006.72l.806.838z"
            opacity=".5"
          />
        </svg>

        {`${notifications.extraNewNotifications} ${label}`}
      </span>
    );
  };

  const setNotificationRef = ({ ref, id }) => {
    notificationsRefs.current[id] = ref;
  };

  const getNotifications = () => {
    if (notifications.notifications.length) {
      return notifications.notifications.map((notification) => (
        <Notification
          key={notification.ID}
          notification={notification}
          reference={(ref) => setNotificationRef({ id: notification.ID, ref })}
          baseUrl={baseUrl}
        />
      ));
    }

    return false;
  };

  const handleExtraNewNotifications = () => {
    // Because React's synthetic event doesn't prevent browser's event propagation.
    setTimeout(() => {
      if (notifications.loadExtraNewNotifications && scrollbar.current) {
        const scrollTop = scrollbar.current.getScrollTop();

        notifications.loadExtraNewNotifications().then(() => {
          if (scrollTop > 0) {
            animate({
              from: scrollTop,
              to: 0,
              duration: 250,
              progress: (progress) => {
                scrollbar.current.scrollTop(progress);
              },
            });
          } else {
            handleScrollbar();
          }
        });
      }
    }, 0);
  };

  useEffect(() => {
    if (!isOverscrollBehaviorEnabled) {
      addWheelListeners();
    }
    addTouchListeners();
    handleScrollbar();

    return () => {
      removeTouchEventListeners();
      if (!isOverscrollBehaviorEnabled) {
        removeWheelEventListeners();
      }
    };
  }, []);

  return (
    <div className={cx("scrollbar-holder")}>
      {!!notifications.extraNewNotifications &&
        !notifications.notificationError && (
          <div
            className={cx("panel", "extra-new")}
            onClick={handleExtraNewNotifications}
            role="presentation"
          >
            {notifications.loadingMoreNotifications ? (
              <div className={cx("spinner-bar")}>
                <Preloader />
              </div>
            ) : (
              getExtraNewNotificationsText()
            )}
          </div>
        )}

      <Scrollbars
        autoHeight
        autoHeightMax={
          mobile && androidVersion && androidVersion >= 4.6
            ? `calc(100vh - ${mobileControlsHeightPx}px)`
            : `calc(100vh - ${desktopControlsHeightPx}px)` || 223
        }
        autoHeightMin={110}
        onScrollFrame={handleScrollFrame}
        className={cx("scrollpanel")}
        ref={scrollbar}
      >
        <div className={cx("notification-content")}>
          <div>
            {getNotifications()}

            {shouldShowPreloader() && (
              <div className={cx("preload-spinner-bar")}>
                <Preloader width={24} />
              </div>
            )}
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

NotificationsScrollbar.propTypes = {
  mobile: PropTypes.bool,
};

export default observer(NotificationsScrollbar);
